import axios from "axios";
import {Category} from "@/utils/Category";
import {Product} from "~/utils/Product";
import {User} from "~/utils/User";
import {Cart, cartToJson} from "~/utils/Cart";
import {useUserDataStorage, type RequestData} from "~/store/userdata";
import {Article} from "~/utils/Article";
import {BlogArticle} from "~/utils/BlogArticle";
import {BlogCategory} from "~/utils/BlogCategory";

export function useModyApi() {
  const userDataStore = useUserDataStorage();
  const { user } = storeToRefs(userDataStore)
  const baseUrl = useRuntimeConfig().public.apiUrl
  const api = axios.create({
    baseURL: baseUrl,
    headers: user.value?.token ? { Authorization: `Bearer ${user.value?.token}` } : {}
  });

  async function get(url:string){
    return await api.get(url);
  }
  async function post(url:string,data:any){
    return await api.post(url,data);
  }
  async function update(url:string, data:any){
    try {
      return await api.put(url,{
        data: data
      });
    }
    catch (e) {
      console.log(e)
      return e
    }
  }
  async function put(url:string, data:any){
    try {
      return await api.put(url,
        data
    );
    }
    catch (e) {
      console.log(e)
      return e
    }
  }
  async function create(url:string, data:any){
    try {
      return await api.post(url,{
        data: data
      });
    }
    catch (e) {
      throw e
    }
  }
  async function remove(url:string){
    return await api.delete(url);
  }

  async function getProductsPaginated(page:number, pageSize:number,filterString:string) {
    page++
    const response = await api.get("products?populate=Images,categories,articles&pagination[page]="+page+"&pagination[pageSize]="+pageSize
        +"&filters[Name][$contains]="+filterString);
    const totalRecords = response.data.meta.pagination.total
    const products:Product[] = response.data.data.map((value) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      return tempProduct;
    });

    return {products, totalRecords}
  }

  async function getArticlesPaginated(page:number, pageSize:number,filterString:string) {
    page++
    const response = await api.get("articles?populate=product&pagination[page]="+page+"&pagination[pageSize]="+pageSize
    +"&filters[$or][0][article_number][$contains]="+filterString
        +"&filters[$or][1][product][Name][$contains]="+filterString);
    const totalRecords = response.data.meta.pagination.total
    const articles:Article[] = response.data.data.map((value) => {
      const tempArticle = new Article()
      tempArticle.parseJson(value)
      return tempArticle;
    });

    return {articles, totalRecords}
  }


  async function getCategory(id:Number):Promise<Category>{
    const category:Category = new Category()
    const response = await api.get(
        "categories/"+id+
        "?populate=parent,categories"
    );
    category.parseJson(response.data.data)
    return category
  }
  async function getCategories() {
    const response = await api.get(
        "categories?pagination[pageSize]=100&populate[0]=categories&populate[categories][populate][0]=categories&populate=parent"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: Category[] = [];
    responseCats.forEach((value) => {
      if (!value.attributes.parent.data) {
        const tempCategory = new Category();
        tempCategory.parseJson(value);
        categories.push(tempCategory);
      }
    });

    return categories;
  }

  async function getAllCategories(){
    const response = await api.get(
      "categories?pagination[pageSize]=100&populate[0]=categories&populate[categories][populate][0]=categories&populate=parent"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: Category[] = [];
    responseCats.forEach((value) => {
        const tempCategory = new Category();
        tempCategory.parseJson(value);
        categories.push(tempCategory);
    });

    return categories;
  }
  
  async function getCategoryProducts(categoryId: number): Promise<Product[]> {
    // Get Products of Main category
    const response = await api.get(
        "publicProducts" +
        "?filters[categories][id][$in]="+categoryId+"&populate=Images,categories.categories"
    );
    const mainCategoryProducts = response.data.data.map((value) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      return tempProduct;
    });
    const category = await getCategory(categoryId);
    if (category && category.subCategories.length > 0) {
      // Fetch Products of Subcategories in parallel
      const subCategoriesPromises = category.subCategories.map(subCategory =>
          getCategoryProducts(subCategory.id)
      );
      const subCategoriesProducts = await Promise.all(subCategoriesPromises);

      // Flatten and concatenate subcategories products
      const subCategoriesProductsFlat = subCategoriesProducts.flat();

      // Combine main category products with subcategory products
      return mainCategoryProducts.concat(subCategoriesProductsFlat);
    }

    return mainCategoryProducts;
  }

  async function getProduct(productId: number): Promise<Product> {
    const tempProduct: Product = new Product();
    const response = await api.get("publicProducts/" + productId + "?populate=%2A");
    tempProduct.parseJson(response.data.data);
    return tempProduct;
  }
  async function searchProducts(searchString: string): Promise<Product[]> {
    const tempProducts: Product[] = []
    const response = await api.get("publicProducts?populate=Images,categories&filters[$or][0][Name][$contains]="+searchString+"&filters[$or][1][Description][$contains]="+searchString);
    response.data.data.forEach((value) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      tempProducts.push(tempProduct);
    });
    return tempProducts;
  }
  async function searchProductsByName(searchString: string): Promise<Product[]> {
    const tempProducts: Product[] = []
    const response = await api.get("products?populate=Images,categories&filters[$or][0][Name][$contains]="+searchString);
    response.data.data.forEach((value) => {
      const tempProduct = new Product();
      tempProduct.parseJson(value);
      tempProducts.push(tempProduct);
    });
    return tempProducts;
  }
  async function login(email: string, password: string): Promise<User> {
    try {
      const response = await api.post("/auth/local", {
        identifier: email,
        password: password,
      });

      const user = new User()
      user.parseJson(response.data.user)
      user.token = response.data.jwt
      return user;
    }
    catch (e){
      const error = JSON.parse(e.request.response).error
      if(error.name == "ValidationError"){
        throw "ValidationError"
      }
      else{
        throw "Other Error"
      }
    }
  }

  async function searchUsersByUsername(searchString: string): Promise<User[]> {
    const tempUsers: User[] = []
    const response = await api.get("users?filters[$or][0][username][$contains]="+searchString);
    console.log(response)
    response.data.forEach((value:any) => {
      const tempUser = new User();
      tempUser.parseJson(value);
      tempUsers.push(tempUser);
    });
    return tempUsers;
  }

  async function getUserRole(){
    try {
      const response = await api.get("users/me?populate=*");
      console.log(response.data)
      return response.data.role
    }
    catch (e) {
      console.log(e)
    }
  }
  async function getUserCart(): Promise<Cart>{
    let cart:Cart = new Cart()
    const userDataStore = useUserDataStorage();
    if(!userDataStore.loggedIn){return cart}
    const { user } = storeToRefs(userDataStore)
    try {
      const response = await api.get("users/me?populate=Cart.product,Cart.product.Images");
      cart.parseJSON(response.data.Cart)
      return cart
    }
    catch (e) {
      console.log(e)
    }

    return cart
  }
  async function updateCart(cart:Cart){
    if(!useUserDataStorage().loggedIn){
      return
    }
    const userDataStore = useUserDataStorage();
    const { user } = storeToRefs(userDataStore)
    try {
      const response = await api.put("users/"+user.value?.id,{
            Cart:cart.toApiStruct()
          },
          {
            headers:{
              Authorization: `Bearer ${user.value?.token}`,
            },
          }
      );
    }
    catch (e) {
      console.log(e)
    }
  }
  async function sendRequest(requestData:Ref<RequestData>,cart:Cart){
    try {

      let response = await api.get("sendRequest",{
        params:{
          "name":requestData.value.name,
          "address":requestData.value.address,
          "city":requestData.value.city,
          "company_name":requestData.value.company_name,
          "eventDays":requestData.value.eventDays,
          "description":requestData.value.description,
          "phone":requestData.value.telephone,
          "email":requestData.value.email,
          "fromDate":requestData.value.date_from.toLocaleDateString(),
          "toDate":requestData.value.date_to.toLocaleDateString(),
          "cart": JSON.stringify(cartToJson(cart)),
          "subscribeNewsletter":requestData.value.subscribeNewsletter ? requestData.value.subscribeNewsletter : false,
        },
        timeout:5000
      })
      if(response.status === 200){
        return true
      }
      else{
        return false
      }
    }
    catch (e) {
      console.log(e)
      return false
    }
  }

  async function getFeaturedBlogArticles(page:number, pageSize:number,filterString:string) {
    page++
    return await getBlogArticleRequest("blog-articles?populate=Image&pagination[page]="+page+"&pagination[pageSize]="+pageSize+"&sort=publishedAt:desc")
  }
  async function getBlogArticlesOfCategory(page:number, pageSize:number,filterString:string,categoryId:number) {
    page++
    return await getBlogArticleRequest("blog-articles?populate=Image&pagination[page]="+page+"&pagination[pageSize]="+pageSize+"&filters[blog_category][$eq]="+categoryId+"&sort=publishedAt:desc")
  }

  async function getBlogArticleRequest(url:string){
    const response = await api.get(url);
    const totalRecords = response.data.meta.pagination.total
    const blogArticles:BlogArticle[] = response.data.data.map((value) => {
      const temp = new BlogArticle();
      temp.parseJson(value);
      return temp;
    });
    return {blogArticles, totalRecords}
  }

  async function getBlogArticle(id:number) {
    const response = await api.get("blog-articles/"+id+"?populate=Image");
    const blogArticle = new BlogArticle();
    blogArticle.parseJson(response.data.data)

    return blogArticle
  }

  async function getBlogCategories() {
    const response = await api.get(
        "blog-categories?pagination[pageSize]=100"
    );
    const responseCats = response.data.data as {
      id: number;
      attributes: { [key: string]: any };
    }[];
    let categories: BlogCategory[] = [];
    responseCats.forEach((value) => {
      const tempCategory = new BlogCategory();
      tempCategory.parseJson(value);
      categories.push(tempCategory);
    });

    return categories;
  }

  async function getBlogCategory(id:number) {
    const response = await api.get(
        "blog-categories/"+id
    );

    let category = new BlogCategory();
    category.parseJson(response.data.data)

    return category;
  }


  return {baseUrl, api,get,post,put,update,remove,create,getCategory, getCategories,getAllCategories, getCategoryProducts, getProduct, getProductsPaginated, getArticlesPaginated, login,searchProducts,getUserRole, getUserCart,
    updateCart,sendRequest,searchProductsByName,getFeaturedBlogArticles,getBlogArticle,getBlogCategories,getBlogArticlesOfCategory,getBlogCategory,searchUsersByUsername};
}
